// NoteStyles.js - Helper functions for styling notes based on CSS classes


// Color palette configuration from memory
const COLOR_PALETTE = [
  { id: 'orange', hex: '#FF9800', name: 'Orange', suffix: '' }, // Original, no suffix
  { id: 'red', hex: '#F44336', name: 'Red', suffix: 'red' },
  { id: 'green', hex: '#4ade80', name: 'Green', suffix: 'green' },
  { id: 'blue', hex: '#60a5fa', name: 'Blue', suffix: 'blue' }
];

/**
 * Creates a className string for a note based on its properties
 * @param {Object} params - Note parameters
 * @returns {string} - CSS class string for styling
 */
export const getNoteClassNames = ({
  noteValue,
  isRoot,
  isModeColorNote,
  noteIsInPattern,
  isInScale = true, // Default to true since TouchableNoteMarker is only rendered for in-scale notes
  isSelected,
  isArpeggio,
  isChordTone,
  isChordHighlightNote,
  arpeggioDegree,
  selectedColor = 'orange', // Default to orange if no color specified
  visibleColors = ['orange', 'red', 'green', 'blue'] // Default all colors visible
}) => {
  // Basic note classes
  let classes = [
    'string-row-note-marker',
    `string-row-note-${noteValue}`
  ];
  
  // Add semantic classes
  if (isRoot) classes.push('string-row-note-root');
  if (isModeColorNote) classes.push('string-row-note-mode-color');
  if (noteIsInPattern) classes.push('string-row-note-pattern');
  if (isSelected) classes.push('string-row-note-playing');
  
  // Pattern/off-pattern suffix
  const patternSuffix = noteIsInPattern ? 'inscale' : 'offscale';
  
  // Highlighting class hierarchy (from highest to lowest priority)
  if (isSelected) {
    // Get the color suffix from the selected color
    const colorInfo = COLOR_PALETTE.find(c => c.id === selectedColor);
    const colorSuffix = colorInfo ? colorInfo.suffix : '';

    // Apply the appropriate class based on the selected color
    if (colorSuffix) {
      classes.push(`hl_selected-note-${patternSuffix}-${colorSuffix}`);
    } else {
      classes.push(`hl_selected-note-${patternSuffix}`);
    }
    
    // Add a data attribute for the color for filtering visibility
    classes.push(`note-color-${selectedColor}`);
  // Chord highlighting should have higher priority than arpeggio
  } else if (isChordHighlightNote) {
    classes.push(`hl_chord-highlight-${patternSuffix}`);
    // Log for debugging    
  } else if (isArpeggio) {
    classes.push(`hl_arpeggio-${patternSuffix}`);
    // Log for debugging    
  } 
  //else if (isChordTone) {
    // Chord tones have priority after arpeggios
    //classes.push(`hl_chord-tone-${patternSuffix}`);
 // }
   else if (isRoot) {
    classes.push(`hl_root-note-${patternSuffix}`);
  } else if (isModeColorNote) {
    classes.push(`hl_mode-color-${patternSuffix}`);
  } else {
    classes.push(`hl_scale-note-${patternSuffix}`);
  }
  
  // Add hidden class if the note's color is not in the visible colors list
  if (isSelected && !visibleColors.includes(selectedColor)) {
    classes.push('note-hidden');
  }
  
  return classes.join(' ');
};

/**
 * Creates data attributes for a note based on its properties
 * @param {Object} params - Note parameters
 * @returns {Object} - Data attributes object
 */
export const getNoteDataAttributes = ({
  isRoot,
  isModeColorNote,
  noteIsInPattern,
  isSelected,
  isArpeggio,
  isChordTone,
  isChordHighlightNote,
  arpeggioDegree,
  selectedColor = 'orange'
}) => {
  return {
    'data-isroot': isRoot ? 'true' : 'false',
    'data-ismodecolor': isModeColorNote ? 'true' : 'false',
    'data-ispattern': noteIsInPattern ? 'true' : 'false',
    'data-isplaying': isSelected ? 'true' : 'false',
    'data-isarpeggio': isArpeggio ? 'true' : 'false',
    'data-ischordtone': isChordTone ? 'true' : 'false',
    'data-ischordHighlight': isChordHighlightNote ? 'true' : 'false',
    'data-arpeggio-degree': arpeggioDegree || '',
    'data-color': isSelected ? selectedColor : ''
  };
};

/**
 * Export the color palette for use in other components
 */
export const NOTE_COLORS = COLOR_PALETTE;
